import "./RecordingDialog.css";

import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import ReplayIcon from '@mui/icons-material/Replay';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import React, { MouseEventHandler, useMemo, useRef, useState } from "react";
import { Button } from "reactstrap";
import { ModalWindow } from "src/app/shared/components/modal-window/ModalWindow";
import useRecorder, { videoDimensionConstraint } from "./hooks/useRecorder";
import useStopwatch from "./hooks/useStopwatch";
import AudioRecordingPreview from "./audio-recording-preview/AudioRecordingPreview";
import VideoRecordingPreview from "./video-recording-preview/VideoRecordingPreview";
import Loader from "../loader/Loader";
import { LoaderType } from "../loader/~types/LoaderType";
import { useTranslation } from "react-i18next";
import { showErrorToastr } from "../../helpers/toastr-helper/ToastrHelper";

export enum RecordingType {
    Audio = "Audio",
    Video = "Video"
}

export interface IRecordingInfo {
    type: RecordingType,
    recording: File,
    metadata: {
        duration?: number,
        videoDimension?: number,
    }
}

interface IRecordingDialogProps {
    onRecordingSend(recording: IRecordingInfo): void,
}

const MAX_VIDEO_SIZE_BYTES = 12000000;

function RecordingDialog(props: IRecordingDialogProps) {
    const { t } = useTranslation();
    
    const [isRecordingModalVisible, setIsRecordingModalVisible] = useState<boolean>(false);
    const [selectedRecordingType, setSelectedRecordingType] = useState<RecordingType>(RecordingType.Audio);

    const [audioRecording, setAudioRecording] = useState<IRecordingInfo | undefined>();
    const [videoRecording, setVideoRecording] = useState<IRecordingInfo | undefined>();
    const videoLiveRef = useRef<HTMLVideoElement>(null);

    const stopwatch = useStopwatch();
    const recorder = useRecorder({videoLiveRef, onStop: _onRecordingStopped, onStart: _onRecordingStart});

    const formattedMinutes = useMemo(() => String(stopwatch.minutesElapsed).padStart(2, '0'), [stopwatch.minutesElapsed]);
    const formattedSeconds = useMemo(() => String(stopwatch.secondsElapsed).padStart(2, '0'), [stopwatch.secondsElapsed]);

    const isCurrentRecordingExist = selectedRecordingType === RecordingType.Audio && audioRecording ||
        selectedRecordingType === RecordingType.Video && videoRecording
    
    return (
    
            <React.Fragment>
                <div className="recording-dialog__container">
                    <VideoCameraFrontIcon className="recording-dialog__toggle" 
                        onClick={_openModal}/>
                    <ModalWindow
                        className="recording-dialog__modal"
                        isOpen={isRecordingModalVisible}
                        onClose={_closeModal}
                        header={_getRecordingModalHeader()}
                        body={selectedRecordingType === RecordingType.Audio ? 
                            <AudioRecordingPreview audioRecording={audioRecording}/> : 
                            <VideoRecordingPreview isRecording={recorder.isRecording} videoLiveRef={videoLiveRef} videoRecording={videoRecording}/>
                        }
                        footer={_getRecordingModalFooter()}/>
                </div>
            </React.Fragment>
    );

    function _onRecordingStart(){
        stopwatch.start();
    }
    
    function _onRecordingStopped(recording: File) {
        const elapsed = stopwatch.stop();
        if (selectedRecordingType === RecordingType.Audio) {
            setAudioRecording({
                type: selectedRecordingType,
                recording,
                metadata: {
                    duration: elapsed
                }
            });
        }
        else
        {
            if (recording.size <= MAX_VIDEO_SIZE_BYTES)
            {
                setVideoRecording({
                    type: selectedRecordingType,
                    recording,
                    metadata: {
                        duration: elapsed,
                        videoDimension: videoDimensionConstraint,
                    }  
                });
            }
            else
            {
                showErrorToastr(t("error.recordingVideoTooLarge"));
            }
        }
    }

    function _openModal()
    {
        recorder.reset();
        setIsRecordingModalVisible(true);
    }

    function _closeModal(afterSending: boolean = false)
    {
        const shouldClarify = !afterSending && (audioRecording || videoRecording || recorder.isRecording);
        if (shouldClarify && !confirm(t("chat.recordingNotSent")))
        {
            return;
        }
        setIsRecordingModalVisible(false);
        _clearRecording(RecordingType.Audio);
        _clearRecording(RecordingType.Video);
        recorder.forceStop();
    }

    function _startRecording(e: React.MouseEvent<HTMLButtonElement>)
    {
        if (isCurrentRecordingExist && !confirm(t("chat.resetRecordingConfirmation")))
        {
            e.currentTarget?.blur();
            return;
        }
        _clearRecording(selectedRecordingType);
        recorder.start(selectedRecordingType === RecordingType.Audio);
    }

    function _stopRecording(e: React.MouseEvent<HTMLButtonElement>)
    {
        e.currentTarget?.blur();
        recorder.stop();
    }

    function _clearRecording(type: RecordingType)
    {
        if (type === RecordingType.Audio) {
            setAudioRecording(undefined);
        }
        else {
            setVideoRecording(undefined);
        }
    }

    function _sendRecording()
    {
        const selectedRecording = selectedRecordingType === RecordingType.Audio ? audioRecording : videoRecording;

        if (selectedRecording)
        {
            props.onRecordingSend(selectedRecording);

            _clearRecording(RecordingType.Audio);
            _clearRecording(RecordingType.Video);
            _closeModal(true);
        }
    }

    function _getRecordingControls() {
        return (
            <div className="recording-controls__container">
                {
                    recorder.isRecording ? (
                        <>
                            <Button className="recording-control stop-recording__btn"
                                onClick={_stopRecording} 
                                disabled={!recorder.isRecording}>
                                <StopCircleIcon className="stop-icon"/>
                                {t('common.stop')}
                            </Button>
                            <div className="recording__stopwatch">
                                {formattedMinutes}:{formattedSeconds}
                            </div>
                        </>
                    ) : (
                        <Button className="recording-control start-recording__btn" 
                            onClick={_startRecording} disabled={recorder.isRecording || recorder.isBusy}>
                            {recorder.isBusy && <Loader type={LoaderType.Grow}/>}
                            {!recorder.isBusy && (
                                <>
                                    {isCurrentRecordingExist ? 
                                            <ReplayIcon className="record-icon"/> : 
                                            <FiberManualRecordIcon className="record-icon"/>}
                                    {t('common.start')}
                                </>
                            )}
                        </Button>
                    )
                }
            </div>
        )
    }
    
    function _getRecordingModalHeader() {
        return (
            <div className="recording-modal__header">
                <div className="recording-modal__header-close-btn-container">
                    <Button
                        className="recording-modal__close-button red"
                        onClick={() => {
                            _closeModal();
                        }}>
                        <i className="material-icons">
                            close
                        </i>
                    </Button>
                </div>
                <div className="recording-modal__header-tab-container">
                    <div className={"recording-modal__tab" + 
                        (selectedRecordingType === RecordingType.Audio ? " active" : "") +
                        (selectedRecordingType !== RecordingType.Audio && recorder.isRecording ? " disabled" : "")}
                        onClick={() => !recorder.isRecording && setSelectedRecordingType(RecordingType.Audio)}>
                        {t('common.audio')}
                    </div>
                    <div className={"recording-modal__tab" + 
                            (selectedRecordingType === RecordingType.Video ? " active" : "") +
                            (selectedRecordingType !== RecordingType.Video && recorder.isRecording ? " disabled" : "")}
                        onClick={() => !recorder.isRecording && setSelectedRecordingType(RecordingType.Video)}>
                        {t('common.video')}
                    </div>
                </div>
            </div>
        )
    }

    function _getRecordingModalFooter() {
        return (
            <div className="recording-modal-footer__container">
                {_getRecordingControls()}
                <Button id="send__btn" 
                    disabled={!_canSendRecording()}
                
                    onClick={_sendRecording}>
                    {t('common.send')}
                </Button>

            </div>
        )
    }

    function _canSendRecording() : boolean
    {
        if (selectedRecordingType === RecordingType.Audio && !audioRecording ||
            selectedRecordingType === RecordingType.Video && !videoRecording
        )
        {
            return false;
        }
        if (selectedRecordingType === RecordingType.Video && videoRecording && videoRecording.recording.size > MAX_VIDEO_SIZE_BYTES)
        {
            return false;
        }

        return true;
    }
}

export default RecordingDialog;
